import { loadStripe } from '@stripe/stripe-js';
import { SubscriptionPlan } from './subscriptionService';

// Initialize Stripe with your publishable key
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
let stripePromise: Promise<any> | null = null;

// Get the Stripe instance (singleton pattern)
export const getStripe = () => {
  if (!stripePromise) {
    if (!stripePublishableKey) {
      console.error('Stripe publishable key is missing');
      return Promise.reject('Stripe publishable key is missing');
    }
    stripePromise = loadStripe(stripePublishableKey);
  }
  return stripePromise;
};

// Price IDs for each plan - these should match your Stripe dashboard
export const STRIPE_PRICE_IDS: Record<SubscriptionPlan, string | undefined> = {
  [SubscriptionPlan.FREE]: undefined, // Free tier has no Stripe price ID
  [SubscriptionPlan.STANDARD]: 'price_1R8pMkE3WmYLaWVEigV0zxA9', // Standard plan price ID (Live Mode)
  [SubscriptionPlan.PREMIUM]: 'price_1R8pNHE3WmYLaWVEwKFJ9kie',  // Premium plan price ID (Live Mode)
};

// Create checkout session
export const createCheckoutSession = async (
  priceId: string,
  userId: string,
  userEmail?: string
): Promise<{ sessionId: string } | null> => {
  try {
    // Use a relative URL to avoid hardcoding the server address
    const apiUrl = '/api/create-checkout-session';
    console.log('Creating checkout session at:', apiUrl, { priceId, userId, userEmail });
    
    // Add a timestamp to prevent caching issues
    const timestamp = new Date().getTime();
    
    const response = await fetch(`${apiUrl}?t=${timestamp}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        userId,
        userEmail,
      }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return null;
  }
};

// Redirect to Stripe Checkout
export const redirectToCheckout = async (
  planType: SubscriptionPlan,
  userId: string,
  userEmail?: string
) => {
  try {
    console.log(`Starting checkout process for plan: ${planType}`);
    
    // Get the price ID for the selected plan
    const priceId = STRIPE_PRICE_IDS[planType];
    console.log(`Retrieved price ID for ${planType}:`, priceId);
    
    if (!priceId) {
      console.error(`No price ID found for plan ${planType}`);
      throw new Error(`No price ID found for plan ${planType}`);
    }

    // Create a checkout session
    console.log(`Creating checkout session with price ID: ${priceId}`);
    const response = await createCheckoutSession(priceId, userId, userEmail);
    console.log('Checkout session creation response:', response);
    
    if (!response || !response.sessionId) {
      console.error('Failed to create checkout session - empty or invalid response');
      throw new Error('Failed to create checkout session');
    }

    const { sessionId } = response;
    console.log(`Successfully created session ID: ${sessionId}`);

    // Redirect to Stripe Checkout
    console.log('Initializing Stripe redirect');
    const stripe = await getStripe();
    const { error } = await stripe.redirectToCheckout({ sessionId });
    
    if (error) {
      console.error('Stripe checkout error:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in redirectToCheckout:', error);
    throw error;
  }
};

// Check subscription status
export const checkSubscriptionStatus = async (
  userId: string
): Promise<{
  isActive: boolean;
  planType: SubscriptionPlan | null;
}> => {
  try {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const response = await fetch(`/api/check-subscription?userId=${userId}&t=${timestamp}`);
    
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return await response.json();
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return {
      isActive: false,
      planType: null,
    };
  }
}; 